import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import MyProfileImage from '../assets/Profile/myprofile2.jpg';
import CV from '../assets/CV_NakhalanAtqiyaArifinPutra.pdf';
import { useScrollAnimation } from './hooks/ScrollAnimation';
import HobbiesSection from './profile/HobbiesSection';
import AchievementsSection from './profile/AchievementsSection';
import SkillsSection from './profile/SkillsSection';
import EducationSection from './profile/EducationSection';
import ContactModal from './ContactModal'; 
import { useTheme } from '../context/ThemeContext';
import {
  FaGithub, FaWhatsapp, FaLinkedin, FaEnvelope,
  FaInstagram, FaMedium, FaSpotify
} from 'react-icons/fa';

const AboutMeTyping = ({ text, speed = 30, isDark }) => {
  const [displayedText, setDisplayedText] = useState("");
  useEffect(() => {
    setDisplayedText("");
    let i = 0;
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <p className={`${isDark ? 'text-slate-300' : 'text-gray-700'} leading-relaxed text-sm md:text-base whitespace-pre-line`}>
      {displayedText}
      <span className="animate-pulse">|</span>
    </p>
  );
};

const Profile = () => {
  const { isDark } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({ threshold: 0.03 });
  const { ref: avatarRef, isVisible: avatarVisible } = useScrollAnimation({ threshold: 0.2, repeat: true });
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: bioRef, isVisible: bioVisible } = useScrollAnimation({ threshold: 0.08, repeat: true });

  const calculateAge = useCallback(() => {
    const birthDate = new Date(2007, 5, 17);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  }, []);

  const [age, setAge] = useState(calculateAge());

  useEffect(() => {
    const interval = setInterval(() => setAge(calculateAge()), 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [calculateAge]);

  const socialLinks = [
    { icon: <FaGithub className="text-2xl" />, name: "GitHub", url: "https://github.com/NakhalanAtqiyaAP", color: "bg-gray-800 hover:bg-gray-900" },
    { icon: <FaInstagram className="text-2xl" />, name: "Instagram", url: "https://www.instagram.com/orangejahad/", color: "bg-orange-500 hover:bg-orange-600" },
    { icon: <FaEnvelope className="text-2xl" />, name: "Email", url: "mailto:nakhalanatqiyaarifin@gmail.com", color: "bg-red-500 hover:bg-red-600" },
    { icon: <FaWhatsapp className="text-2xl" />, name: "WhatsApp", url: "https://wa.me/6285715383055", color: "bg-green-500 hover:bg-green-600" },
    { icon: <FaLinkedin className="text-2xl" />, name: "LinkedIn", url: "https://www.linkedin.com/in/nakhalan-atqiya-arifin-putra-4b5b082a3/", color: "bg-blue-700 hover:bg-blue-800" },
    { icon: <FaMedium className="text-2xl" />, name: "Medium", url: "https://medium.com/@nakhalanatqiya", color: "bg-gray-900 hover:bg-gray-800" },
    { icon: <FaSpotify className="text-2xl" />, name: "Spotify", url: "https://open.spotify.com/user/e6wded30e0kqvoessxh67ios3?si=a41ac2b7cb784e64", color: "bg-green-500 hover:bg-green-600" },
  ];

  const aboutText =
    "Saya adalah seorang Website Developer & Game Developer,\n" +
    "saya belajar dunia pemrograman disaat saya menginjak bangku SMK, tepatnya di SMK Wikrama Bogor.\n" +
    "Saat ini saya sedang menempuh pendidikan tinggi di Institut Bisnis dan Informatika Kesatuan, dengan jurusan Teknik Informasi.\n" +
    "Tekad saya masuk kedunia pemrograman adalah membuat studio game saya sendiri.";

  const bioLeftItems = [
    { color: "bg-red-500", label: "Nama Lengkap", value: "Nakhalan Atqiya Arifin Putra" },
    { color: "bg-yellow-500", label: "Usia", value: `${age} Years` },
    { color: "bg-green-500", label: "Lokasi", value: "Bogor, Indonesia" },
  ];

  const bioRightItems = [
    { color: "bg-blue-500", label: "Email", value: "nakhalanatqiyaarifin@gmail.com", isEmail: true },
    { color: "bg-purple-500", label: "Phone", value: "+62 857-1538-3055" },
    { color: "bg-pink-500", label: "Spesialisasi", value: "Game Developer & Website Developer" },
  ];

  // Dark mode styles
  const cardBg = isDark ? 'bg-[#0D1520]' : 'bg-white';
  const bioBg = isDark ? 'bg-[#111C2C]' : 'bg-gray-50';
  const innerCardBg = isDark ? 'bg-[#0D1520]' : 'bg-white';
  const titleColor = isDark ? 'text-white' : 'text-gray-800';
  const subtitleColor = isDark ? 'text-blue-400' : 'text-blue-600';
  const labelColor = isDark ? 'text-slate-300' : 'text-gray-700';
  const valueColor = isDark ? 'text-slate-400' : 'text-gray-600';
  const sectionTitleColor = isDark ? 'text-white' : 'text-gray-800';
  const dividerColor = isDark ? 'border-[#1E3A5F]' : 'border-gray-200';

  return (
    <>
      <section
        id="profile"
        ref={sectionRef}
        className="py-8 md:py-12 px-4 pixel-grid min-h-screen flex items-center"
        style={{
          opacity: sectionVisible ? 1 : 0,
          transform: sectionVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
        }}
      >
        <div className="container mx-auto max-w-5xl">
          <div className={`pixel-border ${cardBg} p-6 md:p-8 lg:p-10 relative space-y-8`}>

            {/* BARIS 1: Header + Avatar + Info Biodata & Pendidikan */}
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8 md:gap-10 lg:gap-12">
              <div className="flex flex-col items-center lg:items-start w-full lg:w-auto shrink-0">
                <div
                  ref={avatarRef}
                  className={`w-36 h-36 md:w-44 md:h-44 pixel-border ${isDark ? 'bg-[#111C2C]' : 'bg-gray-100'} overflow-hidden relative mb-4 group`}
                  style={{
                    opacity: avatarVisible ? 1 : 0,
                    transform: avatarVisible ? 'scale(1)' : 'scale(0.85)',
                    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
                  }}
                >
                  <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                    <img
                      src={MyProfileImage}
                      alt="Nakhalan Atqiya Arifin Putra"
                      className="w-full h-full object-cover transition duration-500 group-hover:scale-110"
                      style={{ imageRendering: "pixelated" }}
                    />
                  </div>
                </div>

                <HobbiesSection />
                <AchievementsSection />
              </div>

              {/* Kolom Kanan: Biodata, Tentang Saya, dan Riwayat Pendidikan */}
              <div className="flex-1 w-full">
                <div
                  ref={headerRef}
                  className="text-center lg:text-left mb-6"
                  style={{
                    opacity: headerVisible ? 1 : 0,
                    transform: headerVisible ? 'translateY(0)' : 'translateY(-20px)',
                    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
                  }}
                >
                  <h2 className={`text-2xl md:text-3xl font-bold ${titleColor} mb-2 pixel-font`}>
                    <span className="glitch-text" data-text="HELLO_WORLD">HELLO_WORLD</span>
                  </h2>
                  <h3 className={`text-xl ${subtitleColor} mb-4 pixel-font`}>{"// Website & Game Developer"}</h3>
                  <div className="h-1 w-20 bg-blue-600 mx-auto lg:mx-0 mb-4" />
                </div>

                <div
                  ref={bioRef}
                  className={`${bioBg} pixel-border p-5 md:p-6`}
                  style={{
                    opacity: bioVisible ? 1 : 0,
                    transform: bioVisible ? 'translateY(0)' : 'translateY(24px)',
                    transition: 'opacity 0.6s ease-out 0.05s, transform 0.6s ease-out 0.05s',
                  }}
                >
                  {/* Grid Biodata */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-5">
                    <div className="space-y-3">
                      {bioLeftItems.map((item, i) => (
                        <div key={i} className="flex items-start">
                          <div className={`w-4 h-4 ${item.color} mr-3 flex-shrink-0 mt-0.5`} />
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-semibold ${labelColor}`}>{item.label}</div>
                            <div className={`${valueColor} text-sm md:text-base leading-snug`}>{item.value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-3">
                      {bioRightItems.map((item, i) => (
                        <div key={i} className="flex items-start">
                          <div className={`w-4 h-4 ${item.color} mr-3 flex-shrink-0 mt-0.5`} />
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-semibold ${labelColor}`}>{item.label}</div>
                            <div className={`${valueColor} text-sm md:text-base leading-snug ${item.isEmail ? 'break-all' : ''}`}>
                              {item.value}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tentang Saya */}
                  <div>
                    <h4 className={`text-lg font-semibold ${sectionTitleColor} mb-3 flex items-center`}>
                      <div className="w-4 h-4 bg-blue-600 mr-2 flex-shrink-0" />
                      Tentang Saya
                    </h4>
                    <div className={`${innerCardBg} pixel-border p-4`}>
                      <AboutMeTyping text={aboutText} speed={15} isDark={isDark} />
                    </div>
                  </div>

                  {/* Komponen Riwayat Pendidikan */}
                  <EducationSection />
                </div>
              </div>
            </div>

            {/* BARIS 2: Skills Section Full Width */}
            <div className={`w-full pt-2 border-t-2 border-dashed ${dividerColor}`}>
              <SkillsSection />
            </div>

            {/* BARIS 3: Tombol CV & Medsos */}
            <div className={`w-full pt-4 border-t-2 border-dashed ${dividerColor}`}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
                <motion.a
                  href={CV}
                  download="CV_NakhalanAtqiyaArifinPutra.pdf"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-1/2 text-center pixel-button bg-blue-600 text-white py-3 text-sm md:text-base hover:bg-blue-700 font-semibold cursor-pointer block"
                >
                  DOWNLOAD CV
                </motion.a>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsModalOpen(true)}
                  className="w-full sm:w-1/2 text-center pixel-button bg-green-600 text-white py-3 text-sm md:text-base hover:bg-green-700 font-semibold cursor-pointer block"
                >
                  Media Sosial
                </motion.button>
              </div>
            </div>

          </div>
        </div>
      </section>

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        socialLinks={socialLinks}
      />
    </>
  );
};

export default Profile;
